/* Park & Swoope – base layer (ps-). Own code. */
(() => {
  const root = document.documentElement;

  function initReveal() {
    if (!root.classList.contains('ps-reveal-enabled')) return;
    if (root.classList.contains('shopify-design-mode')) return;
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const sections = Array.from(document.querySelectorAll('main .shopify-section'));
    const viewportBottom = window.innerHeight;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('ps-revealed');
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    const pending = new Set();
    for (const section of sections) {
      // Leave anything already on screen alone (no flash, no LCP delay).
      if (section.getBoundingClientRect().top < viewportBottom) continue;
      section.classList.add('ps-reveal');
      observer.observe(section);
      pending.add(section);
    }

    // Safety net: a very fast scroll can jump past a section between observer
    // samples. Reveal anything whose top is above the bottom of the screen.
    let queued = false;
    const sweep = () => {
      queued = false;
      for (const section of pending) {
        if (section.classList.contains('ps-revealed')) {
          pending.delete(section);
        } else if (section.getBoundingClientRect().top < window.innerHeight) {
          section.classList.add('ps-revealed');
          observer.unobserve(section);
          pending.delete(section);
        }
      }
      if (!pending.size) document.removeEventListener('scroll', onScroll, true);
    };
    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(sweep);
    };
    if (pending.size) document.addEventListener('scroll', onScroll, { capture: true, passive: true });
  }

  /* Compact header: toggles html.ps-header-compact past a scroll threshold.
     Horizon scrolls .page-wrapper on desktop and the document on mobile, so we
     listen in the capture phase and read whichever element scrolled. */
  function initHeaderShrink() {
    const marker = document.querySelector('[data-ps-header-shrink]');
    if (!marker) return;
    const threshold = Number(marker.dataset.threshold) || 80;
    const hysteresis = 40; // avoids flicker when the header height changes
    let ticking = false;

    const scrollTopOf = (target) => {
      if (!target || target === document || target === document.documentElement || target === document.body) {
        return window.scrollY || document.documentElement.scrollTop || 0;
      }
      return target.scrollTop || 0;
    };

    const update = (target) => {
      const top = scrollTopOf(target);
      const compact = root.classList.contains('ps-header-compact');
      if (!compact && top > threshold) root.classList.add('ps-header-compact');
      else if (compact && top < Math.max(0, threshold - hysteresis)) root.classList.remove('ps-header-compact');
    };

    document.addEventListener(
      'scroll',
      (event) => {
        const target = event.target;
        if (target !== document && !(target instanceof Element && target.matches('.page-wrapper, html, body'))) return;
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          update(target);
        });
      },
      { capture: true, passive: true }
    );

    update(document.querySelector('.page-wrapper') || document);
  }

  /* ---------- Product page swatches (blocks/ps-swatches.liquid) ----------
     Decorates Horizon's own variant buttons for the color option. Horizon
     re-renders the picker after each change, so a MutationObserver re-applies
     the decoration; it only adds what is missing, so it settles immediately. */
  function buildSwatch(config, value) {
    const data = config.values[value.trim().toLowerCase()] || {};
    const swatch = document.createElement('span');
    swatch.className = 'ps-sw';
    swatch.setAttribute('aria-hidden', 'true');

    const media = document.createElement('span');
    media.className = 'ps-sw__media';
    if (data.hex) media.style.backgroundColor = data.hex;
    if (data.image) {
      const img = document.createElement('img');
      img.src = data.image;
      img.alt = '';
      img.decoding = 'async';
      if (data.ratio) {
        // Reserve the photo's own proportions so the tile has height before the image loads.
        media.style.aspectRatio = String(data.ratio);
        img.width = 240;
        img.height = Math.round(240 / Number(data.ratio));
      }
      media.append(img);
    } else if (!data.hex) {
      media.textContent = value.trim().charAt(0);
      media.classList.add('ps-sw__media--initial');
    }
    swatch.append(media);

    if (config.style === 'tiles' && (config.showName || config.showPrice)) {
      const meta = document.createElement('span');
      meta.className = 'ps-sw__meta';
      if (config.showName) {
        const name = document.createElement('span');
        name.className = 'ps-sw__name';
        name.textContent = value;
        meta.append(name);
      }
      if (config.showPrice && data.price) {
        const price = document.createElement('span');
        price.className = 'ps-sw__price';
        price.textContent = data.price;
        meta.append(price);
      }
      swatch.append(meta);
    }
    return swatch;
  }

  /* A color option with a single value: Horizon renders plain text, we add one selected tile. */
  function decorateSingleSwatch(config, scope) {
    for (const single of scope.querySelectorAll('.variant-option[data-testid="variant-option-single"]')) {
      if (single.querySelector('.ps-sw')) continue;
      const valueEl = single.querySelector('[data-testid="variant-option-single-value"]');
      if (!valueEl) continue;
      const optionName = single.firstChild && single.firstChild.nodeType === 3 ? single.firstChild.textContent.trim() : '';
      if (optionName.toLowerCase() !== config.optionName) continue;
      const value = valueEl.textContent.trim();
      single.classList.add('ps-sw-fieldset', `ps-sw-fieldset--${config.style}`, 'ps-sw-single');
      const legend = document.createElement('span');
      legend.className = 'ps-sw-single__legend';
      legend.textContent = optionName;
      if (config.showSelected) {
        const selected = document.createElement('span');
        selected.className = 'ps-sw-selected';
        selected.textContent = value;
        legend.append(selected);
      }
      single.firstChild.textContent = '';
      valueEl.hidden = true;
      const tile = document.createElement('span');
      tile.className = 'ps-sw-label ps-sw-label--static';
      tile.append(buildSwatch(config, value));
      single.prepend(legend);
      single.append(tile);
    }
  }

  function decorateSwatches(config, scope) {
    decorateSingleSwatch(config, scope);
    for (const fieldset of scope.querySelectorAll('fieldset.variant-option')) {
      const first = fieldset.querySelector('input[data-option-name]');
      if (!first || first.dataset.optionName.trim().toLowerCase() !== config.optionName) continue;

      fieldset.classList.add('ps-sw-fieldset', `ps-sw-fieldset--${config.style}`);

      const legend = fieldset.querySelector('legend');
      const checked = fieldset.querySelector('input:checked');
      if (legend && config.showSelected) {
        let selected = legend.querySelector('.ps-sw-selected');
        if (!selected) {
          selected = document.createElement('span');
          selected.className = 'ps-sw-selected';
          legend.append(selected);
        }
        const text = checked ? checked.value : '';
        if (selected.textContent !== text) selected.textContent = text;
      }

      for (const label of fieldset.querySelectorAll('label.variant-option__button-label')) {
        const input = label.querySelector('input');
        if (!input) continue;
        label.classList.add('ps-sw-label');
        if (label.querySelector('.ps-sw')) continue;

        const swatch = buildSwatch(config, input.value);
        if (config.style === 'circles') label.title = input.value;
        label.append(swatch);
      }
    }
  }

  function initSwatches() {
    for (const script of document.querySelectorAll('script[data-ps-swatches]')) {
      if (script.dataset.psReady) continue;
      let config;
      try {
        config = JSON.parse(script.textContent);
      } catch (error) {
        continue;
      }
      script.dataset.psReady = 'true';
      const scope = script.closest('.shopify-section') || document;
      let queued = false;
      const run = () => {
        queued = false;
        decorateSwatches(config, scope);
        updateEta(scope);
      };
      run();
      new MutationObserver(() => {
        if (queued) return;
        queued = true;
        queueMicrotask(run);
      }).observe(scope, { childList: true, subtree: true, attributes: true, attributeFilter: ['checked', 'data-current-checked'] });
      scope.addEventListener('change', () => queueMicrotask(run));
    }
  }

  /* ---------- Product card swatches (snippets/ps-card-swatches.liquid) ---------- */
  function initCardSwatches() {
    const canHover = window.matchMedia('(hover: hover)').matches;

    const preview = (button) => {
      const card = button.closest('product-card, .product-card');
      if (!card) return;
      const gallery = card.querySelector('.card-gallery') || card;
      if (button.dataset.previewSrc) {
        let img = gallery.querySelector('img.ps-card-preview');
        if (!img) {
          img = document.createElement('img');
          img.className = 'ps-card-preview';
          img.alt = '';
          img.decoding = 'async';
          img.sizes = '(min-width: 750px) 25vw, 50vw';
          gallery.append(img);
        }
        img.srcset = button.dataset.previewSrcset || '';
        img.src = button.dataset.previewSrc;
        gallery.classList.add('ps-card-preview-active');
      }
      if (button.dataset.variantUrl) {
        const variant = new URL(button.dataset.variantUrl, location.origin);
        const handlePath = variant.pathname.slice(variant.pathname.lastIndexOf('/products/'));
        for (const link of card.querySelectorAll('a[href]')) {
          const url = new URL(link.href, location.origin);
          if (!url.pathname.endsWith(handlePath)) continue;
          url.searchParams.set('variant', variant.searchParams.get('variant'));
          link.href = url.pathname + url.search;
        }
      }
      for (const other of card.querySelectorAll('.ps-card-swatch[aria-pressed]')) other.removeAttribute('aria-pressed');
      button.setAttribute('aria-pressed', 'true');
    };

    document.addEventListener('click', (event) => {
      const button = event.target.closest('.ps-card-swatch');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      preview(button);
    });

    if (canHover) {
      document.addEventListener('pointerover', (event) => {
        const button = event.target.closest?.('.ps-card-swatch');
        if (button) preview(button);
      });
    }
  }

  /* ---------- Hero slideshow (sections/ps-hero-slideshow.liquid) ---------- */
  function initSlideshows(scope = document) {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    for (const root of scope.querySelectorAll('[data-ps-slideshow]')) {
      if (root.psSlideshow) root.psSlideshow.destroy();
      const slides = Array.from(root.querySelectorAll('[data-ps-slide]'));
      if (slides.length < 2) continue;
      const dots = Array.from(root.querySelectorAll('[data-ps-dot]'));
      const delay = (Number(root.dataset.speed) || 6) * 1000;
      const autoplay = root.dataset.autoplay === 'true' && !reduceMotion;
      let index = 0;
      let timer = null;
      let paused = false;

      const show = (next) => {
        index = (next + slides.length) % slides.length;
        slides.forEach((slide, i) => {
          const active = i === index;
          slide.classList.toggle('is-active', active);
          if (active) slide.removeAttribute('aria-hidden');
          else slide.setAttribute('aria-hidden', 'true');
          slide.inert = !active;
        });
        dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
      };
      const stop = () => {
        clearInterval(timer);
        timer = null;
      };
      const start = () => {
        stop();
        if (autoplay && !paused && !document.hidden) timer = setInterval(() => show(index + 1), delay);
      };
      const go = (next) => {
        show(next);
        start();
      };

      const onClick = (event) => {
        if (event.target.closest('[data-ps-prev]')) go(index - 1);
        else if (event.target.closest('[data-ps-next]')) go(index + 1);
        else {
          const dot = event.target.closest('[data-ps-dot]');
          if (dot) go(Number(dot.dataset.psDot));
        }
      };
      const pause = () => {
        paused = true;
        stop();
      };
      const resume = () => {
        paused = false;
        start();
      };
      let touchX = null;
      const onTouchStart = (event) => (touchX = event.touches[0].clientX);
      const onTouchEnd = (event) => {
        if (touchX === null) return;
        const dx = event.changedTouches[0].clientX - touchX;
        touchX = null;
        if (Math.abs(dx) > 40) go(index + (dx < 0 ? 1 : -1));
      };
      const onVisibility = () => (document.hidden ? stop() : start());

      root.addEventListener('click', onClick);
      root.addEventListener('mouseenter', pause);
      root.addEventListener('mouseleave', resume);
      root.addEventListener('focusin', pause);
      root.addEventListener('focusout', resume);
      root.addEventListener('touchstart', onTouchStart, { passive: true });
      root.addEventListener('touchend', onTouchEnd, { passive: true });
      document.addEventListener('visibilitychange', onVisibility);

      show(0);
      start();

      root.psSlideshow = {
        go,
        destroy() {
          stop();
          root.removeEventListener('click', onClick);
          root.removeEventListener('mouseenter', pause);
          root.removeEventListener('mouseleave', resume);
          root.removeEventListener('focusin', pause);
          root.removeEventListener('focusout', resume);
          root.removeEventListener('touchstart', onTouchStart);
          root.removeEventListener('touchend', onTouchEnd);
          document.removeEventListener('visibilitychange', onVisibility);
        },
        slides,
      };
    }
  }

  /* ---------- Shipping date (blocks/ps-shipping-eta.liquid) ---------- */
  function updateEta(scope = document) {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    for (const root of scope.querySelectorAll('[data-ps-eta]')) {
      const out = root.querySelector('[data-ps-eta-date]');
      if (!out) continue;
      const skipSat = root.dataset.skipSat === 'true';
      const skipSun = root.dataset.skipSun === 'true';
      const skip = (d) => (skipSat && d.getDay() === 6) || (skipSun && d.getDay() === 0);
      const date = new Date();
      let left = Number(root.dataset.days) || 0;
      while (left > 0) {
        date.setDate(date.getDate() + 1);
        if (!skip(date)) left -= 1;
      }
      while (skip(date)) date.setDate(date.getDate() + 1);
      const text = `${days[date.getDay()]}. ${date.getDate()}`;
      if (out.textContent !== text) out.textContent = text;
    }
  }

  /* ---------- Cart drawer upsell (snippets/ps-cart-extras.liquid) ----------
     Adds the product with the same cart event Horizon's own product form uses,
     so the drawer, cart count and discount bar all re-render the normal way. */
  async function addUpsell(form) {
    const button = form.querySelector('button[type="submit"]');
    const id = Number(new FormData(form).get('id'));
    if (!id || button?.getAttribute('aria-busy') === 'true') return;
    button?.setAttribute('aria-busy', 'true');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    const routes = (window.Theme && window.Theme.routes) || {};
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    let events = null;
    let deferred = null;
    try {
      events = await import('@shopify/events');
      deferred = events.CartLinesUpdateEvent.createPromise();
      form.dispatchEvent(
        new events.CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(id), quantity: 1 }],
          promise: deferred.promise,
        })
      );
    } catch (error) {
      events = null;
    }

    try {
      const response = await fetch(routes.cart_add_url || `${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      if (result.status) throw new Error(result.description || result.message || 'Add to cart failed');
      const cart = await fetch(`${root}cart.js`).then((r) => r.json());
      if (deferred && events) {
        deferred.resolve({
          cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'ps-cart-upsell', itemCount: 1, sections: result.sections },
        });
      } else {
        window.location.reload();
      }
    } catch (error) {
      if (deferred) deferred.reject(error);
      button?.removeAttribute('aria-busy');
      console.warn('[ps-cart-upsell]', error);
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('[data-ps-upsell-form]');
    if (!form) return;
    event.preventDefault();
    addUpsell(form);
  });

  function init() {
    initReveal();
    updateEta();
    initSlideshows();
    initHeaderShrink();
    initSwatches();
    initCardSwatches();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Theme editor: re-run when the compact header section is added or edited.
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('[data-ps-header-shrink]')) initHeaderShrink();
    initSwatches();
    initSlideshows(event.target);
  });
  document.addEventListener('shopify:block:select', (event) => {
    initSwatches();
    const root = event.target.closest('[data-ps-slideshow]');
    if (root && root.psSlideshow) root.psSlideshow.go(root.psSlideshow.slides.indexOf(event.target));
  });
})();
